import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonSkeletonText,
  IonText,
  IonBadge,
  IonSearchbar,
  IonRefresherContent,
  IonGrid,
  IonRow,
  IonCol,
  IonModal,
  IonFab,
  IonList,
  IonCard,
  IonCardContent,
  IonItem,
  IonAvatar,
  IonLabel,
} from '@ionic/react';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { receipt, chevronUp, chevronDown, addOutline, chevronBack, closeOutline, close, cashOutline, calendarOutline, chevronDownOutline, swapVertical, receiptOutline, searchOutline } from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import { useIonRouter } from '@ionic/react';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../auth/firebase';
import { brevoEmailState } from '../utils/brevoEmailState';
import { getZohoInvoices, getInvoicesFromFirestore, syncInvoicesCache, isInvoiceAchPending, getBrevoInvoiceEmailStatuses } from '../services/zohoInvoicingService';
import type { ZohoInvoice, BrevoInvoiceEmailStatus } from '../services/zohoInvoicingService';
import { getInvoicesMetadata } from '../services/invoiceMetadataService';
import styles from './InvoiceList.module.scss';
import { applySearchOverlayPosition } from './bookingSystem/overlayPosition';

import Invoice from './Invoice';
import CreateInvoice from './CreateInvoice';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';

type SortField = 'invoiceNumber' | 'amount' | 'status' | 'customer' | 'dateCreated' | 'dateIssued' | 'datePaid';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'cancelled' | 'due' | 'paid' | 'totals' | 'completed' | 'refunded' | 'inProgress' | 'overdue' | 'recurring';

interface PortalDropdownOption {
  label: string;
  value: string;
  sublabel?: string;
}

// Same anchored-portal mechanism as Contact.tsx's / PriceCalculator.tsx's
// usePickerDropdown/PickerDropdown — portals an IonCard into an in-page
// portalRef container (absolute-positioned relative to it) instead of relying
// on IonPopover's shadow-DOM ::part(content), which doesn't reliably take a
// custom background in this app. The double-rAF read is needed since ion-item
// can still be mid-upgrade (shadow content not yet rendered) on first paint.
// `align: 'start'` anchors the card's left edge to the trigger's left edge and
// matches the trigger's width (right for wide, full-row triggers, e.g. Contact.tsx's
// billing config rows). `align: 'end'` anchors the card's right edge to the
// trigger's right edge instead and lets the card size itself (via CSS
// min/max-width) — needed for narrow icon-only triggers like the sort button,
// where matching the trigger's own width would squeeze the menu text to nothing.
type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  align: 'start' | 'end' = 'start',
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const top = box.bottom - anchor.top + 4;
      if (align === 'end') {
        return { top, right: anchor.right - box.right };
      }
      return { top, left: box.left - anchor.left, width: box.width };
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: PortalDropdownOption[];
  value?: string;
  onSelect: (value: string) => void;
  align?: 'start' | 'end';
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, align = 'start' }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, align);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
      <IonList lines="none" className={styles.customDropdownListInner}>
        {options.map(opt => (
          <IonItem
            key={opt.value}
            button
            lines="none"
            detail={false}
            className={`${styles.customDropdownItem} ${opt.value === value ? styles.customDropdownItemActive : ''}`}
            onClick={() => onSelect(opt.value)}
          >
            <IonLabel>{opt.label}</IonLabel>
            {opt.sublabel && <IonText slot="end" className={styles.customDropdownSublabel}>{opt.sublabel}</IonText>}
          </IonItem>
        ))}
      </IonList>
    </IonCard>,
    portalNode,
  );
};

interface InvoiceListProps {
  onClose?: () => void;
  initialData?: ZohoInvoice[];
  initialSearch?: string;
  // When set, scopes the list to one Zoho customer (e.g. a single contact's
  // invoices) instead of the full company-wide list. Bypasses the unfiltered
  // Firestore listener entirely in favor of a direct customer_id query — the
  // list still owns its own loading state from first render either way, so
  // callers never need to pre-fetch/gate rendering externally themselves.
  customerId?: string;
}

interface MappedInvoice extends BookingItem {
  zohoInvoiceId: string;
  isRecurring: boolean;
  dueDate?: string;
  isEmailed: boolean;
  isViewedByClient: boolean;
  clientViewedTime: string;
  isViewedInMail: boolean;
  mailFirstViewedTime: string;
  currentSubStatus: string;
  isAchPending: boolean;
  zohoStatus: string;
  rawData: ZohoInvoice;
}

/** Map a Zoho Books invoice to the BookingItem shape used by this component */
function mapZohoInvoice(inv: ZohoInvoice): MappedInvoice {
  const zohoStatus = inv.status ?? 'unpaid';
  const isPaid = zohoStatus === 'paid';

  const dateCreated = inv.date ? new Date(inv.date + 'T00:00:00') : new Date();
  const dateUpdated = dateCreated;

  // Real creation time (what Zoho paginates by) — the invoice date above is the service date and
  // can be far older, which made late pages sort above/below earlier ones while scrolling.
  // Zoho sends offsets like "-0500"; normalise to "-05:00" so every WebView parses it.
  const createdTime = inv.created_time ? new Date(inv.created_time.replace(/([+-]d{2})(d{2})$/, '$1:$2')) : null;
  const createdAt = createdTime && !isNaN(createdTime.getTime()) ? createdTime : dateCreated;

  const statusMap: Record<string, BookingItem['status']> = {
    paid:            'completed',
    unpaid:          'due',
    overdue:         'overdue',
    sent:            'due',
    viewed:          'due',
    draft:           'confirmed',
    void:            'cancelled',
    partially_paid:  'due',
  };
  const status = statusMap[zohoStatus] ?? 'due';
  const dateStr = inv.date ?? dateCreated.toISOString().split('T')[0];

  const billing = inv.billing_address ?? {};
  const contactName = inv.customer_name ?? 'Unknown';
  const contactEmail = inv.email
    ?? inv.contact_persons_associated?.[0]?.contact_person_email
    ?? '';
  const contactPhone = billing.phone ?? '';
  const contactAddress = [billing.address, billing.city, billing.state].filter(Boolean).join(', ');

  const firstLine = inv.line_items?.[0];
  const itemName = firstLine?.name ?? firstLine?.description ?? '';
  const title = itemName || i18n.t('pages.InvoiceList.invoice2', { invoice_number: inv.invoice_number });

  const lowerItem = itemName.toLowerCase();
  const knownTypes = Object.keys(serviceColorMap) as BookingItem['serviceType'][];
  const serviceType: BookingItem['serviceType'] =
    knownTypes.find(t => t.toLowerCase() === itemName.toLowerCase()) ?? 'Other';

  const isRecurring = lowerItem.includes('recurring') || lowerItem.includes('weekly') || lowerItem.includes('bi-weekly') || lowerItem.includes('monthly');
  const frequency: BookingItem['frequency'] = isRecurring ? 'monthly' : 'one-time';
  const price = inv.total ?? 0;

  return {
    id: inv.invoice_number,
    title,
    type: 'booking',
    startTime: dateCreated,
    endTime: dateUpdated,
    date: dateStr,
    serviceType,
    status,
    notes: inv.notes,
    location: contactAddress || undefined,
    contactId: inv.customer_id ?? '',
    contact: {
      id: inv.customer_id ?? '',
      name: contactName,
      email: contactEmail,
      phone: contactPhone,
      address: contactAddress,
    },
    staff: undefined,
    price,
    isPaid,
    frequency,
    createdAt,
    updatedAt: dateUpdated,
    zohoInvoiceId: inv.invoice_id,
    isRecurring,
    dueDate: inv.due_date,
    isEmailed: inv.is_emailed ?? false,
    isViewedByClient: inv.is_viewed_by_client ?? false,
    clientViewedTime: inv.client_viewed_time ?? '',
    isViewedInMail: inv.is_viewed_in_mail ?? false,
    mailFirstViewedTime: inv.mail_first_viewed_time ?? '',
    currentSubStatus: inv.current_sub_status ?? '',
    isAchPending: isInvoiceAchPending(inv),
    zohoStatus,
    rawData: inv,
  };
}

const InvoiceList: React.FC<InvoiceListProps> = ({ onClose = undefined, initialData, initialSearch, customerId }) => {
  const { t: tr } = useTranslation();
  const [invoices, setInvoices] = useState<MappedInvoice[]>(() =>
    initialData ? initialData.map(mapZohoInvoice) as MappedInvoice[] : []
  );
  const [isLoading, setIsLoading] = useState(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(initialSearch ?? '');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('dateCreated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  // Page number lives in a ref (not state) so loadInvoices keeps a stable identity: the Firestore
  // listener effect depends on it, and a changing identity re-subscribed the listener after every
  // page load, resetting the list to the first 25 and triggering another load-more.
  const pageRef = useRef(1);

  // Email status from Brevo for the invoices on screen, looked up live (as each page loads) — not stored.
  const [brevoStatuses, setBrevoStatuses] = useState<Record<string, BrevoInvoiceEmailStatus>>({});
  const brevoAskedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const items = invoices
      .filter(i => i.isEmailed && !brevoAskedRef.current.has(i.zohoInvoiceId))
      .map(i => {
        const raw = i.rawData as ZohoInvoice;
        return { id: i.zohoInvoiceId, invoiceNumber: raw.invoice_number ?? '', email: raw.customer_email ?? raw.email ?? '', date: raw.date };
      })
      .filter(i => i.invoiceNumber && i.email);
    if (items.length === 0) return;
    items.forEach(i => brevoAskedRef.current.add(i.id));
    getBrevoInvoiceEmailStatuses(items.map(({ invoiceNumber, email, date }) => ({ invoiceNumber, email, date })))
      .then(found => setBrevoStatuses(prev => ({ ...prev, ...found })))
      .catch(err => {
        console.warn('Brevo email status lookup failed:', err);
        items.forEach(i => brevoAskedRef.current.delete(i.id)); // let a later render retry
      });
  }, [invoices]);
  const loadingMoreRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchResults, setSearchResults] = useState<MappedInvoice[] | null>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const PAGE_SIZE = 25;
  const navigate = useNavigate();
  const router = useIonRouter();

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const sortTriggerRef = useRef<HTMLButtonElement>(null);
  const pickerPortalRef = useRef<HTMLIonGridElement>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Refs for PositionedRefresher
  const titleCardElementRef = useRef<HTMLIonGridElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const fabRef = useRef<HTMLIonFabElement>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  }, [onClose, router]);

  // Overlay positioning for morphing search

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  const handleSearchToggle = () => {
    if (isSearchVisible) {
      setSearchText('');
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220); // Match animation duration

      // Blur input and hide keyboard
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((inputEl: HTMLInputElement) => {
            try { inputEl.blur(); } catch { }
            try { Keyboard.hide(); } catch { }
          }).catch(() => { try { Keyboard.hide(); } catch { } });
        }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (getter) {
            getter().then((inputEl: HTMLInputElement) => {
              try { inputEl.focus(); } catch { }
            }).catch(() => { });
          }
        });
      });
    }
  };

  // Enrich already-displayed invoices with Firestore metadata (due-date overrides)
  // in the background — non-fatal, doesn't block or extend the loading state.
  const enrichWithMetadata = useCallback(async (zohoInvoices: ZohoInvoice[]) => {
    try {
      const ids = zohoInvoices.map(inv => inv.invoice_id);
      const metaMap = await getInvoicesMetadata(ids);
      setInvoices(prev => prev.map(inv => {
        const meta = metaMap.get(inv.zohoInvoiceId);
        if (meta?.dueDate) return { ...inv, dueDate: meta.dueDate };
        return inv;
      }));
    } catch {
      // Non-fatal
    }
  }, []);

  const loadInvoices = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setIsLoading(true);
        setError(null);
        pageRef.current = 1;
      } else {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setIsLoadingMore(true);
      }

      const currentPage = reset ? 1 : pageRef.current + 1;
      let zohoInvoices: ZohoInvoice[];
      let fromCache = false;

      if (currentPage === 1) {
        // Page 1: read from Firestore cache (~100ms)
        zohoInvoices = await getInvoicesFromFirestore();
        // If cache is empty fall back to Zoho directly
        if (zohoInvoices.length === 0) {
          zohoInvoices = await getZohoInvoices({ page: 1 });
        } else {
          fromCache = true;
        }
      } else {
        // Page 2+: fetch directly from Zoho
        zohoInvoices = await getZohoInvoices({ page: currentPage });
      }

      const mapped = zohoInvoices.map(mapZohoInvoice) as MappedInvoice[];
      // The cache no longer always holds exactly PAGE_SIZE docs (it's a persistent
      // mirror now, not force-capped), so its count can't be used to detect "more
      // pages exist". Assume more when the cache returned anything — page 2 hits
      // Zoho live and will correctly report exhaustion if there really isn't more.
      const more = fromCache ? zohoInvoices.length > 0 : zohoInvoices.length >= PAGE_SIZE;

      if (reset) {
        setInvoices(mapped);
        setHasMore(more);
        pageRef.current = 1;
        setIsLoading(false);
      } else {
        setInvoices(prev => {
          const seen = new Set(prev.map(inv => inv.zohoInvoiceId));
          return [...prev, ...mapped.filter(inv => !seen.has(inv.zohoInvoiceId))];
        });
        setHasMore(more);
        pageRef.current = currentPage;
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      }

      enrichWithMetadata(zohoInvoices);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(tr('pages.InvoiceList.failedToLoadInvoicesPleaseTry'));
      setIsLoading(false);
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [enrichWithMetadata, tr]);

  // Initial load + live updates share one direct Firestore listener instead of
  // a separate Cloud Function round-trip for page 1: `getInvoicesFromFirestore()`
  // goes through a callable function (network hop + possible cold start) just to
  // read the same `zoho/invoices/list` collection this listener already opens,
  // so the first snapshot (served from local cache almost instantly) now supplies
  // the initial page directly. Only falls back to the slower loadInvoices() path
  // when the cache is genuinely empty or the listener itself fails.
  useEffect(() => {
    if (initialData && initialData.length > 0) return;

    if (customerId) {
      let cancelled = false;
      // Deferred via a resolved-promise microtask (not called synchronously
      // in the effect body) — the fetch below already only starts inside
      // this same continuation, so ordering is preserved.
      Promise.resolve().then(() => {
        if (cancelled) return;
        setIsLoading(true);
        getZohoInvoices({ customer_id: customerId })
          .then(results => {
            if (cancelled) return;
            setInvoices(results.map(mapZohoInvoice) as MappedInvoice[]);
            setHasMore(false);
          })
          .catch(err => {
            console.error('Error loading customer invoices:', err);
            if (!cancelled) setInvoices([]);
          })
          .finally(() => { if (!cancelled) setIsLoading(false); });
      });
      return () => { cancelled = true; };
    }

    // Ordered by Zoho's created_time (what the page-2+ Zoho fetches paginate by), NOT _sortKey:
    // the webhook rewrites _sortKey on every update, so an old invoice that was just paid jumped
    // into this "newest 25" and later pages then filled in the gap above it. Docs without a
    // created_time are skipped by this query; an empty result falls back to the live Zoho fetch.
    const q = query(
      collection(db, 'zoho', 'invoices', 'list'),
      orderBy('created_time', 'desc'),
      limit(25)
    );

    let isFirstSnapshot = true;
    const unsubscribe = onSnapshot(q, (snap) => {
      const cached = snap.docs.map(d => d.data() as ZohoInvoice);

      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        if (cached.length > 0) {
          setInvoices(cached.map(mapZohoInvoice) as MappedInvoice[]);
          setHasMore(true);
          setIsLoading(false);
          enrichWithMetadata(cached);
        } else {
          loadInvoices();
        }
        return;
      }

      if (cached.length > 0) {
        const mapped = cached.map(mapZohoInvoice) as MappedInvoice[];
        setInvoices(prev => {
          const cacheIds = new Set(cached.map(inv => inv.invoice_id));
          const extra = prev.filter(inv => !cacheIds.has(inv.zohoInvoiceId));
          return [...mapped, ...extra];
        });
      }
    }, () => {
      // Listener itself failed — fall back so the page still loads and surfaces errors.
      loadInvoices();
    });

    return unsubscribe;
  }, [customerId, initialData, loadInvoices, enrichWithMetadata]);

  // Debounced server-side search via Zoho search_text — reaches past whatever
  // pages are currently loaded, since it queries Zoho directly instead of
  // filtering the local `invoices` array.
  // Clearing search results for a too-short query is computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as synchronous setState calls at the top of the effect,
  // which now only handles the actual debounced fetch.
  const [prevSearchTextForResults, setPrevSearchTextForResults] = useState(searchText);
  if (searchText !== prevSearchTextForResults) {
    setPrevSearchTextForResults(searchText);
    if (searchText.trim().length < 2) {
      setSearchResults(null);
      setIsSearchLoading(false);
    }
  }
  useEffect(() => {
    if (searchText.trim().length < 2) return;
    // Deferred via a resolved-promise microtask so setIsSearchLoading(true)
    // isn't traced as synchronous setState within this effect — the debounce
    // timer below still only starts inside this same continuation.
    let timer = 0;
    Promise.resolve().then(() => {
      setIsSearchLoading(true);
      timer = window.setTimeout(async () => {
        try {
          const results = await getZohoInvoices({ search_text: searchText.trim() });
          setSearchResults(results.map(mapZohoInvoice) as MappedInvoice[]);
        } catch (err) {
          console.error('Error searching invoices:', err);
          setSearchResults([]);
        } finally {
          setIsSearchLoading(false);
        }
      }, 400);
    });
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  useEffect(() => {
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (kbHeight: number) => {
      const targetBottom = kbHeight > 0 ? kbHeight + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);

        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKeyboardOffset = (kbHeight: number) => {
      animateBottomTo(kbHeight);
    };

    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        applyKeyboardOffset(info.keyboardHeight || 0);
      }).then(handle => { didShowHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidShow', (info) => {
        applyKeyboardOffset(info.keyboardHeight || 0);
      }).catch(() => { });

      Keyboard.addListener('keyboardWillHide', () => {
        applyKeyboardOffset(0);
      }).then(handle => { didHideHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidHide', () => {
        applyKeyboardOffset(0);
      }).catch(() => { });
    } catch { }

    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleViewportResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        applyKeyboardOffset(offset > 50 ? offset : 0);
      });
    };
    if (vv) vv.addEventListener('resize', handleViewportResize);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setTimeout(handleViewportResize, 300);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch { }
      try { didHideHandle?.remove(); } catch { }
    };
  }, []);

  const refreshInvoices = async () => {
    setIsLoading(true); // show the skeleton while the sync runs
    try {
      await syncInvoicesCache();
      // listener auto-fires when sync writes to Firestore
    } catch {
      await loadInvoices(true); // sync failed — fall back to direct fetch
    } finally {
      setIsLoading(false);
    }
  };

  // Derived via useMemo (not useState+useEffect) so filteredInvoices is always
  // in sync with `invoices` on the very same render — a state+effect pair here
  // caused one render frame where invoices had loaded but filteredInvoices
  // hadn't been recomputed yet, flashing the "No invoices found" empty state.
  const filteredInvoices = useMemo(() => {
    let filtered = searchText.trim() ? (searchResults ?? invoices) : invoices;

    // While the debounced server search hasn't resolved yet (or the query is
    // too short to trigger it), filter what's already loaded so typing still
    // feels instant.
    if (searchText && searchResults === null) {
      filtered = filtered.filter(invoice =>
        invoice.contact.name.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.serviceType.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.title.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.id.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter !== 'all' && statusFilter !== 'totals') {
      filtered = filtered.filter(invoice => {
        if (statusFilter === 'paid') return invoice.isPaid;
        if (statusFilter === 'overdue') {
          // Overdue: has a due date in the past and not paid
          if (invoice.status === 'overdue') return true;
          if (!invoice.isPaid && invoice.dueDate) {
            return invoice.dueDate < new Date().toISOString().split('T')[0];
          }
          return false;
        }
        if (statusFilter === 'due') return invoice.status === 'due';
        if (statusFilter === 'completed') return invoice.status === 'completed';
        if (statusFilter === 'cancelled') return invoice.status === 'cancelled';
        if (statusFilter === 'inProgress') return invoice.status === 'confirmed';
        if (statusFilter === 'refunded') return invoice.status === 'refunded';
        if (statusFilter === 'recurring') return invoice.isRecurring === true;
        return true;
      });
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'invoiceNumber':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'amount':
          comparison = (a.price || 0) - (b.price || 0);
          break;
        case 'customer':
          comparison = a.contact.name.localeCompare(b.contact.name);
          break;
        case 'dateCreated':
          comparison = new Date(a.createdAt || a.date).getTime() - new Date(b.createdAt || b.date).getTime();
          break;
        case 'dateIssued':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'datePaid':
          comparison = new Date(a.updatedAt || a.date).getTime() - new Date(b.updatedAt || b.date).getTime();
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [invoices, searchResults, searchText, statusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (invoice: BookingItem) => {
    if (invoice.isPaid || invoice.status === 'completed') {
      return <IonBadge className={styles.statusPaid}>{tr('pages.InvoiceList.paid')}</IonBadge>;
    }
    if (invoice.status === 'refunded') {
      return <IonBadge className={styles.statusCancelled}>{tr('pages.InvoiceList.refunded')}</IonBadge>;
    }
    if (invoice.status === 'cancelled') {
      return <IonBadge className={styles.statusCancelled}>{tr('pages.InvoiceList.cancelled')}</IonBadge>;
    }
    if (invoice.status === 'confirmed') {
      return <IonBadge className={styles.statusUnpaid}>{tr('pages.InvoiceList.inProgress')}</IonBadge>;
    }
    // ACH submitted but not yet settled — show "pending" instead of due/overdue.
    if ((invoice as MappedInvoice).isAchPending) {
      return <IonBadge className={styles.statusPending}>{tr('pages.InvoiceList.achPending')}</IonBadge>;
    }
    // For due/overdue, calculate days from dueDate
    const mapped = invoice as MappedInvoice;
    if (mapped.dueDate && (invoice.status === 'due' || invoice.status === 'overdue')) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const [y, m, d] = mapped.dueDate.split('-').map(Number);
      const due = new Date(y, m - 1, d);
      const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
      if (diffDays > 0)  return <IonBadge className={styles.statusDue}>{tr('pages.InvoiceList.dueInD', { diffDays })}</IonBadge>;
      if (diffDays === 0) return <IonBadge className={styles.statusDue}>{tr('pages.InvoiceList.dueToday')}</IonBadge>;
      const overdueDays = Math.abs(diffDays);
      return <IonBadge className={styles.statusOverdue}>{tr('pages.InvoiceList.overdueD', { overdueDays })}</IonBadge>;
    }
    if (invoice.status === 'overdue') {
      return <IonBadge className={styles.statusOverdue}>{tr('pages.InvoiceList.overdue')}</IonBadge>;
    }
    return <IonBadge className={styles.statusDue}>{tr('pages.InvoiceList.due')}</IonBadge>;
  };

  const getViewedState = (invoice: MappedInvoice): { icon: string; label: string; color: string } | null => {
    // Brevo (the SMTP relay) reports what happened to the email; Zoho can't see past the handoff.
    const viewed = invoice.zohoStatus === 'viewed' || invoice.isViewedByClient || !!invoice.clientViewedTime || invoice.currentSubStatus === 'viewed';
    const opened = invoice.isViewedInMail || !!invoice.mailFirstViewedTime;
    const brevo = brevoStatuses[(invoice.rawData as ZohoInvoice).invoice_number ?? ''];
    return brevoEmailState(brevo, { viewed, opened, sent: invoice.isEmailed }, k => tr(k));
  };

  // Tone-based status summary for the mobile card badge + status stat column
  // (BookingsListModal's badge system only has success/warning/danger/medium tones).
  const getInvoiceStatusInfo = (invoice: MappedInvoice): { tone: 'success' | 'warning' | 'danger' | 'medium'; badgeLabel: string; shortLabel: string } => {
    if (invoice.isPaid || invoice.status === 'completed') return { tone: 'success', badgeLabel: 'PAID', shortLabel: 'Paid' };
    if (invoice.status === 'refunded') return { tone: 'medium', badgeLabel: 'REFUNDED', shortLabel: 'Refunded' };
    if (invoice.status === 'cancelled') return { tone: 'medium', badgeLabel: 'CANCELLED', shortLabel: 'Cancelled' };
    if (invoice.status === 'confirmed') return { tone: 'warning', badgeLabel: 'IN PROGRESS', shortLabel: 'In Progress' };
    if (invoice.isAchPending) return { tone: 'warning', badgeLabel: 'ACH PENDING', shortLabel: 'Pending' };
    if (invoice.dueDate && (invoice.status === 'due' || invoice.status === 'overdue')) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const [y, m, d] = invoice.dueDate.split('-').map(Number);
      const due = new Date(y, m - 1, d);
      const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
      if (diffDays > 0) return { tone: 'warning', badgeLabel: `DUE IN ${diffDays}D`, shortLabel: 'Due' };
      if (diffDays === 0) return { tone: 'warning', badgeLabel: 'DUE TODAY', shortLabel: 'Due' };
      return { tone: 'danger', badgeLabel: `OVERDUE ${Math.abs(diffDays)}D`, shortLabel: 'Overdue' };
    }
    if (invoice.status === 'overdue') return { tone: 'danger', badgeLabel: 'OVERDUE', shortLabel: 'Overdue' };
    return { tone: 'warning', badgeLabel: 'DUE', shortLabel: 'Due' };
  };

  const BADGE_TONE_CLASS: Record<string, string> = {
    success: 'badgeSuccess',
    warning: 'badgeWarning',
    danger: 'badgeDanger',
    medium: 'badgeMedium',
  };

  const getViewedIcon = (invoice: MappedInvoice) => {
    const state = getViewedState(invoice);
    if (!state) return null;
    return (
      <span title={state.label} className={styles.viewedIcon} style={{ color: state.color }}>
        <IonIcon icon={state.icon} style={{ fontSize: '1.4rem' }} />
      </span>
    );
  };

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string'
      ? new Date(date.length === 10 ? date + 'T00:00:00' : date)
      : date;
    return d.toLocaleDateString(intlLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (date: string | Date | undefined) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return `${d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' })} - ${d.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit' })}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return (
      <IonIcon
        icon={sortDirection === 'asc' ? chevronUp : chevronDown}
        className={styles.sortIcon}
      />
    );
  };

  const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
    all: tr('pages.InvoiceList.allInvoices'), due: tr('pages.InvoiceList.due2'), overdue: tr('pages.InvoiceList.overdue2'), paid: 'Paid',
    cancelled: 'Cancelled', refunded: tr('pages.InvoiceList.refunded2'), inProgress: tr('pages.InvoiceList.inProgress3'),
    recurring: 'Recurring', totals: tr('pages.InvoiceList.totals'), completed: tr('pages.InvoiceList.completed'),
  };

  if (error && !isLoading) {
    const errorContent = (
      <IonContent className={styles.errorContent}>
        <IonGrid className={styles.errorContainer}>
          <IonText color="danger">
            <h2>{tr('pages.InvoiceList.errorLoadingInvoices')}</h2>
            <p>{error}</p>
          </IonText>
          <IonButton fill="outline" onClick={() => loadInvoices(true)}>{tr('pages.InvoiceList.tryAgain')}
          </IonButton>
        </IonGrid>
      </IonContent>
    );
    return onClose
      ? <div className={styles.modalWrapper}>{errorContent}</div>
      : <IonPage className={styles.page}>{errorContent}</IonPage>;
  }

  const pageContent = (
    <>
      <BasePageWithHeader
      ref={baseHeaderRef}
      title={tr('pages.InvoiceList.invoices')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <>
          {onClose ? (
            <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          ) : (
            <IonButton shape='round' className={styles.backButton} onClick={handleClose}>
              <IonIcon icon={chevronBack} />
            </IonButton>
          )}
          <IonButton shape='round' className={onClose ? styles.closeButton : styles.backButton} style={{ visibility: 'hidden' }}/>
          
        </>
      }
      endButtons={
        <>
          <IonButton
            shape='round'
            className={styles.newInvoiceBtn}
            onClick={() => setSelectedInvoiceId('new')}
          >
            <IonIcon icon={addOutline} />
          </IonButton>
          <IonButton shape='round' className={styles.searchButton} onClick={handleSearchToggle}>
            <IonIcon icon={isSearchVisible ? close : searchOutline} />
          </IonButton>
        </>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <PositionedRefresher
          titleRef={titleCardElementRef}
          contentRef={contentElementRef}
          offset={0}
          anchor="bottom"
          id="invoices-refresher"
          pullMin={120}
          pullMax={200}
          pullFactor={0.6}
          disabled={refresherDisabled}
          onIonRefresh={(e: CustomEvent) => {
            const doRefresh = async () => {
              try {
                await refreshInvoices();
              } finally {
                e.detail.complete();
              }
            };
            doRefresh();
          }}
          style={{
            '--ion-color-step-250': 'var(--ion-background-color)',
            '--ion-color-step-200': 'var(--ion-background-color)',
            '--ion-background-color': 'transparent',
          } as React.CSSProperties}
        >
          <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
        </PositionedRefresher>
        <IonGrid
          ref={contentElementRef}
          className={styles.invoicesContainer}
          onScroll={(e) => {
            const el = e.target as HTMLElement;
            const top = el.scrollTop || 0;
            const atTop = top <= 5;
            if (atTop !== wasAtTop.current) {
              wasAtTop.current = atTop;
              setRefresherDisabled(!atTop);
            }
            handleScroll(top);
            // Load more when near bottom
            if (hasMore && !isLoadingMore && !isLoading) {
              const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
              if (nearBottom) loadInvoices(false);
            }
          }}
        >
          <IonGrid ref={titleCardElementRef}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={tr('pages.InvoiceList.invoices')}
              cardClassName={styles.invoicesTitleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.invoicesTitle}
            />
          </IonGrid>

          {/* Filter Bar — same pill trigger + portal-anchored glass dropdown
              card used by Contact.tsx's billing config pickers (PortalDropdown /
              customDropdownList), since IonPopover's shadow-DOM ::part(content)
              won't reliably take a custom background in this app. */}
          <IonGrid className={styles.filterBar}>
            <button
              ref={filterTriggerRef}
              className={styles.periodBtn}
              onClick={() => setShowFilterDropdown(v => !v)}
            >
              {STATUS_FILTER_LABELS[statusFilter]}
              <IonIcon icon={chevronDownOutline} />
            </button>

            <button
              ref={sortTriggerRef}
              className={`${styles.periodBtn} ${styles.sortTrigger}`}
              onClick={() => setShowSortDropdown(v => !v)}
            >
              <IonIcon icon={swapVertical} />
            </button>
          </IonGrid>

          <IonGrid ref={pickerPortalRef} className={styles.pickerPortalRoot} />

          <PortalDropdown
            isOpen={showFilterDropdown}
            anchorRef={filterTriggerRef}
            portalRef={pickerPortalRef}
            onClose={() => setShowFilterDropdown(false)}
            value={statusFilter}
            onSelect={(v) => { setStatusFilter(v as StatusFilter); setShowFilterDropdown(false); }}
            options={([
              { value: 'all', label: tr('pages.InvoiceList.allInvoices') },
              { value: 'due', label: tr('pages.InvoiceList.due2') },
              { value: 'overdue', label: tr('pages.InvoiceList.overdue2') },
              { value: 'paid', label: tr('pages.InvoiceList.paid2') },
              { value: 'cancelled', label: tr('pages.InvoiceList.cancelled2') },
              { value: 'refunded', label: tr('pages.InvoiceList.refunded2') },
              { value: 'recurring', label: tr('pages.InvoiceList.recurring') },
              { value: 'totals', label: tr('pages.InvoiceList.totals') },
            ] as { value: StatusFilter; label: string }[])}
          />

          <PortalDropdown
            isOpen={showSortDropdown}
            anchorRef={sortTriggerRef}
            portalRef={pickerPortalRef}
            onClose={() => setShowSortDropdown(false)}
            value={sortField}
            onSelect={(v) => { handleSort(v as SortField); setShowSortDropdown(false); }}
            align="end"
            options={([
              { field: 'dateCreated', label: 'Date Created' },
              { field: 'dateIssued', label: 'Date Issued' },
              { field: 'datePaid', label: 'Date Paid' },
              { field: 'invoiceNumber', label: 'Invoice Number' },
              { field: 'amount', label: 'Amount' },
              { field: 'customer', label: 'Customer' },
            ] as { field: SortField; label: string }[]).map(o => ({
              label: o.label,
              value: o.field,
              sublabel: sortField === o.field ? (sortDirection === 'asc' ? '↑' : '↓') : undefined,
            }))}
          />

          {/* Filter Tabs */}
          {statusFilter === 'totals' && (
            <IonGrid className={styles.totalsContainer}>
              <IonGrid className={styles.totalsGrid}>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.totalRevenue')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                  </IonCardContent>
                </IonCard>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.paidInvoices')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.filter(inv => inv.isPaid).reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                    <IonText className={styles.totalCount}>
                      {invoices.filter(inv => inv.isPaid).length}{tr('pages.InvoiceList.invoices2')}
                    </IonText>
                  </IonCardContent>
                </IonCard>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.unpaidInvoices')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.filter(inv => !inv.isPaid).reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                    <IonText className={styles.totalCount}>
                      {invoices.filter(inv => !inv.isPaid).length}{tr('pages.InvoiceList.invoices2')}
                    </IonText>
                  </IonCardContent>
                </IonCard>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.completedServices')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.filter(inv => inv.status === 'completed').reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                    <IonText className={styles.totalCount}>
                      {invoices.filter(inv => inv.status === 'completed').length}{tr('pages.InvoiceList.invoices2')}
                    </IonText>
                  </IonCardContent>
                </IonCard>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.inProgress2')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.filter(inv => inv.status === 'confirmed').reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                    <IonText className={styles.totalCount}>
                      {invoices.filter(inv => inv.status === 'confirmed').length}{tr('pages.InvoiceList.invoices2')}
                    </IonText>
                  </IonCardContent>
                </IonCard>
                <IonCard className={styles.totalCard}>
                  <IonCardContent>
                    <IonText className={styles.totalLabel}>{tr('pages.InvoiceList.recurringRevenue')}</IonText>
                    <IonText className={styles.totalValue}>
                      {formatCurrency(invoices.filter(inv => inv.frequency !== 'one-time').reduce((sum, inv) => sum + (inv.price || 0), 0))}
                    </IonText>
                    <IonText className={styles.totalCount}>
                      {invoices.filter(inv => inv.frequency !== 'one-time').length}{tr('pages.InvoiceList.invoices2')}
                    </IonText>
                  </IonCardContent>
                </IonCard>
              </IonGrid>
            </IonGrid>
          )}

          {/* Loading placeholders — same card chrome as the real mobile cards */}
          {isLoading && (
            <IonList className={styles.skeletonList} lines="none" aria-hidden="true">
              {[0, 1, 2, 3, 4].map(i => (
                <IonCard key={`skeleton-${i}`} className={`${styles.card} ${styles.skeletonCard}`}>
                  <IonCardContent>
                    <IonItem lines="none" className={styles.cardTopItem}>
                      <IonSkeletonText animated slot="start" className={`${styles.skeletonBar} ${styles.cardAvatar}`} style={{ borderRadius: '50%' }} />
                      <IonLabel>
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '55%', height: 16, margin: 0 }} />
                      </IonLabel>
                      <IonSkeletonText animated slot="end" className={styles.skeletonBar} style={{ width: 64, height: 20, margin: 0, borderRadius: 10 }} />
                    </IonItem>
                    <IonGrid className={styles.statsRow}>
                      <IonRow>
                        {[0, 1, 2].map(j => (
                          <IonCol key={j} className={styles.statCol}>
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 20, height: 20, margin: 0, borderRadius: 5 }} />
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '65%', height: 12, margin: '4px 0 0' }} />
                            <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 10, margin: '3px 0 0' }} />
                          </IonCol>
                        ))}
                      </IonRow>
                    </IonGrid>
                  </IonCardContent>
                </IonCard>
              ))}
            </IonList>
          )}

          {/* Desktop/Tablet Table View */}
          {!isLoading && statusFilter !== 'totals' && (
            <IonGrid className={styles.tableContainer}>
              <table className={styles.invoiceTable}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('invoiceNumber')}>{tr('pages.InvoiceList.invoiceNumber')} {getSortIcon('invoiceNumber')}
                    </th>
                    <th onClick={() => handleSort('amount')}>{tr('pages.InvoiceList.totalAmount')} {getSortIcon('amount')}
                    </th>
                    <th>{tr('pages.InvoiceList.status')}</th>
                    <th onClick={() => handleSort('customer')}>{tr('pages.InvoiceList.customerName')} {getSortIcon('customer')}
                    </th>
                    <th>{tr('pages.InvoiceList.businessName')}</th>
                    <th>{tr('pages.InvoiceList.currency')}</th>
                    <th onClick={() => handleSort('dateCreated')}>{tr('pages.InvoiceList.dateCreated')} {getSortIcon('dateCreated')}
                    </th>
                    <th onClick={() => handleSort('dateIssued')}>{tr('pages.InvoiceList.dateIssued')} {getSortIcon('dateIssued')}
                    </th>
                    <th onClick={() => handleSort('datePaid')}>{tr('pages.InvoiceList.datePaid')} {getSortIcon('datePaid')}
                    </th>
                    <th>{tr('pages.InvoiceList.viewed')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.zohoInvoiceId}
                      onClick={() => { if (onClose) { setSelectedInvoiceId(invoice.zohoInvoiceId); } else navigate(`/tabs/invoice/${invoice.zohoInvoiceId}`); }}
                      className={styles.invoiceRow}
                    >
                      <td className={styles.invoiceNumber}>{invoice.id}</td>
                      <td className={styles.amount}>{formatCurrency(invoice.price || 0)}</td>
                      <td>{getStatusBadge(invoice)}</td>
                      <td className={styles.customerName}>{invoice.contact?.name || 'â€”'}</td>
                      <td className={styles.businessName}>({invoice.location || tr('pages.InvoiceList.cst')})</td>
                      <td className={styles.currency}>{tr('pages.InvoiceList.usd')}</td>
                      <td className={styles.dateCreated}>{formatDateTime(invoice.createdAt || invoice.date)}</td>
                      <td className={styles.dateIssued}>{formatDate(invoice.date)}</td>
                      <td className={styles.datePaid}>
                        {invoice.isPaid ? formatDate(invoice.updatedAt || invoice.date) : 'N/A'}
                      </td>
                      <td className={styles.viewedCell}>{getViewedIcon(invoice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </IonGrid>
          )}

          {/* Mobile Card View (matches BookingsListModal's bookingCard layout) */}
          {!isLoading && statusFilter !== 'totals' && (
            <IonList className={styles.mobileList} lines="none">
              {filteredInvoices.map((invoice) => {
                const mappedInv = invoice as MappedInvoice;
                const { tone, badgeLabel } = getInvoiceStatusInfo(mappedInv);
                const viewedState = getViewedState(mappedInv);
                return (
                  <IonCard
                    key={invoice.zohoInvoiceId}
                    className={styles.card}
                    button
                    onClick={() => { if (onClose) { setSelectedInvoiceId(mappedInv.zohoInvoiceId); } else navigate(`/tabs/invoice/${mappedInv.zohoInvoiceId}`); }}
                  >
                    <IonCardContent>
                      <IonItem lines="none" className={styles.cardTopItem}>
                        <IonAvatar slot="start" className={styles.cardAvatar}>
                          <img
                            src={getDefaultAvatar(invoice.contact?.name || '')}
                            alt={invoice.contact?.name || ''}
                          />
                        </IonAvatar>
                        <IonLabel>
                          <h2 className={styles.cardName}>
                            {invoice.contact?.name || translateDynamic('Unknown')}
                            {viewedState && (
                              <IonIcon icon={viewedState.icon} className={styles.viewedIcon} style={{ color: viewedState.color }} title={viewedState.label} />
                            )}
                          </h2>
                        </IonLabel>
                        <IonBadge className={`${styles.badge} ${styles[BADGE_TONE_CLASS[tone]]}`} slot="end">
                          {badgeLabel}
                        </IonBadge>
                      </IonItem>

                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          <IonCol className={styles.statCol}>
                            <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                            <IonText><p className={styles.statValue}>{formatCurrency(invoice.price || 0)}</p></IonText>
                            <IonText><p className={styles.statMeta}>{tr('pages.InvoiceList.amount')}</p></IonText>
                          </IonCol>
                          <IonCol className={styles.statCol}>
                            <IonIcon icon={receiptOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                            <IonText><p className={styles.statValue}>{invoice.id}</p></IonText>
                            <IonText><p className={styles.statMeta}>{tr('pages.InvoiceList.invoice')}</p></IonText>
                          </IonCol>
                          <IonCol className={styles.statCol}>
                            <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconPurple}`} />
                            <IonText><p className={styles.statValue}>{formatDate(invoice.date)}</p></IonText>
                            <IonText><p className={styles.statMeta}>{tr('pages.InvoiceList.date')}</p></IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </IonList>
          )}

          {isLoadingMore && (
            <IonGrid className={styles.loadMoreSpinner}>
              <IonSpinner name="bubbles" color="primary" />
            </IonGrid>
          )}
          {!hasMore && invoices.length > 0 && statusFilter !== 'totals' && !searchText && (
            <IonText className={styles.endOfList}>{tr('pages.InvoiceList.allInvoicesLoaded')}</IonText>
          )}

          {!isLoading && filteredInvoices.length === 0 && statusFilter !== 'totals' && (
            <IonGrid className={styles.emptyState}>
              <IonIcon icon={receipt} size="large" color="medium" />
              <h3>{tr('pages.InvoiceList.noInvoicesFound')}</h3>
              <p>{tr('pages.InvoiceList.tryAdjustingYourSearchOrFilters')}</p>
            </IonGrid>
          )}

          {/* Spacer when search is visible so last items aren't hidden */}
          {isSearchVisible && <IonGrid className={styles.searchBarSpacer} />}
        </IonGrid>
      </IonContent>

      {/* Morphing search overlay â€” outside IonContent so position:fixed works on iOS Safari */}
      <IonFab
        ref={el => { overlayRef.current = el as unknown as HTMLElement; fabRef.current = el as unknown as HTMLIonFabElement | null; }}
        className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
        aria-hidden={!isSearchVisible}
      >
        <IonSearchbar
          ref={searchRef}
          value={searchText}
          debounce={50}
          onIonInput={e => setSearchText(e.detail.value ?? '')}
          onIonClear={() => setSearchText('')}
          placeholder={isSearchLoading ? tr('pages.InvoiceList.searchingAllInvoices') : tr('pages.InvoiceList.searchInvoices', { length: invoices.length })}
          animated
          showCancelButton="never"
          showClearButton={searchText ? 'always' : 'never'}
          className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
        />
      </IonFab>
    </BasePageWithHeader>


    {selectedInvoiceId !== null && (
      <IonModal
        isOpen={selectedInvoiceId !== null}
        onDidDismiss={() => { setSelectedInvoiceId(null); }}
        handle={false}
      >
        {selectedInvoiceId === 'new' ? (
          <CreateInvoice
            onClose={() => setSelectedInvoiceId(null)}
            onCreated={() => {
              setSelectedInvoiceId(null);
              loadInvoices();
            }}
            prefill={{ invoiceType: 'INVOICE', lineItems: [] }}
          />
        ) : (
          <Invoice invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />
        )}
      </IonModal>
    )}
    </>
  );

  return onClose
    ? <div className={styles.modalWrapper}>{pageContent}</div>
    : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default InvoiceList;
